import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, AlertTitle, IconButton, Button, Typography, Collapse } from '@mui/material';
import { Close as CloseIcon, Schedule as ScheduleIcon, Warning as WarningIcon, NightsStay as NightsStayIcon, Bolt as BoltIcon, Phone as PhoneIcon } from '@mui/icons-material';
import axios from 'axios';

// Get the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * ServiceStatusBanner Component
 *
 * Displays service availability status to clients:
 * - Warning when service is closing soon
 * - Alert during grace period
 * - After-hours mode indicator
 * - Emergency access indicator
 */
const ServiceStatusBanner = ({ conversationId, onStatusChange, socket, token }) => {
  const [serviceStatus, setServiceStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch service status
  const fetchServiceStatus = useCallback(async () => {
    if (!token) {
      console.log('[ServiceStatusBanner] No token provided, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      const params = conversationId ? { conversationId, skipCache: 'true' } : { skipCache: 'true' };
      console.log('[ServiceStatusBanner] Fetching service status...', params);

      const response = await axios.get(`${API_BASE_URL}/chat/service-status`, {
        params,
        headers: { 'x-auth-token': token }
      });
      console.log('[ServiceStatusBanner] Response:', response.data);

      if (response.data.success) {
        const status = response.data.serviceStatus;
        console.log('[ServiceStatusBanner] Service status:', {
          isServiceOpen: status.isServiceOpen,
          gracePeriod: status.gracePeriod,
          emergencyAccess: status.emergencyAccess
        });
        setServiceStatus(status);
        if (onStatusChange) {
          onStatusChange(status);
        }
      }
    } catch (error) {
      console.error('[ServiceStatusBanner] Error fetching service status:', error);
    } finally {
      setLoading(false);
    }
  }, [conversationId, onStatusChange, token]);

  // Initial fetch and polling
  useEffect(() => {
    fetchServiceStatus();

    // Poll every 30 seconds when service is open
    // Poll every 10 seconds during grace period
    const interval = setInterval(() => {
      fetchServiceStatus();
    }, serviceStatus?.gracePeriod ? 10000 : 30000);

    return () => clearInterval(interval);
  }, [fetchServiceStatus, serviceStatus?.gracePeriod]);

  // Listen for socket events
  useEffect(() => {
    if (!socket) return;

    const handleStatusUpdate = (data) => {
      setServiceStatus(data.status);
      if (onStatusChange) {
        onStatusChange(data.status);
      }
    };

    const handleClosingWarning = (data) => {
      setServiceStatus(prev => ({
        ...prev,
        gracePeriod: {
          inWarningPeriod: true,
          minutesRemaining: data.minutesRemaining,
          closingTime: data.closingTime
        }
      }));
      setDismissed(false); // Show warning again
    };

    const handleServiceClosed = () => {
      fetchServiceStatus();
      setDismissed(false);
    };

    socket.on('server:tos:status_update', handleStatusUpdate);
    socket.on('server:tos:closing_warning', handleClosingWarning);
    socket.on('server:tos:service_closed', handleServiceClosed);
    socket.on('server:tos:service_opened', fetchServiceStatus);
    socket.on('server:tos:emergency_access_granted', fetchServiceStatus);
    socket.on('server:tos:emergency_access_expired', fetchServiceStatus);

    return () => {
      socket.off('server:tos:status_update', handleStatusUpdate);
      socket.off('server:tos:closing_warning', handleClosingWarning);
      socket.off('server:tos:service_closed', handleServiceClosed);
      socket.off('server:tos:service_opened', fetchServiceStatus);
      socket.off('server:tos:emergency_access_granted', fetchServiceStatus);
      socket.off('server:tos:emergency_access_expired', fetchServiceStatus);
    };
  }, [socket, fetchServiceStatus, onStatusChange]);

  // Don't show banner if loading or dismissed (for dismissible types)
  if (loading) return null;
  if (!serviceStatus) return null;

  // Service is open and no warnings - no banner needed
  if (serviceStatus.isServiceOpen && !serviceStatus.gracePeriod && !serviceStatus.emergencyAccess) {
    return null;
  }

  // Emergency Access Banner (green)
  if (serviceStatus.emergencyAccess) {
    return (
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        icon={<BoltIcon />}
      >
        <AlertTitle>Emergency Access Active</AlertTitle>
        Full AI Therapist available until {formatTime(serviceStatus.emergencyAccess.expiresAt)}
        {serviceStatus.emergencyAccess.grantedBy && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Granted by {serviceStatus.emergencyAccess.grantedBy}
          </Typography>
        )}
      </Alert>
    );
  }

  // Grace Period Warning (yellow/orange)
  if (serviceStatus.gracePeriod?.inWarningPeriod && !dismissed) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        icon={<ScheduleIcon />}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setDismissed(true)}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
      >
        <AlertTitle>Service Closing Soon</AlertTitle>
        Service closes in {serviceStatus.gracePeriod.minutesRemaining} minute
        {serviceStatus.gracePeriod.minutesRemaining !== 1 ? 's' : ''} at{' '}
        {serviceStatus.gracePeriod.closingTime || 'the scheduled time'}.
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          You can continue your current conversation.
        </Typography>
      </Alert>
    );
  }

  // Soft Close Period (red/alert)
  if (serviceStatus.gracePeriod?.inSoftClosePeriod) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        icon={<WarningIcon />}
      >
        <AlertTitle>Service Has Closed</AlertTitle>
        You have {serviceStatus.gracePeriod.minutesRemaining} minute
        {serviceStatus.gracePeriod.minutesRemaining !== 1 ? 's' : ''} to finish your conversation.
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          After that, limited support will be available.
        </Typography>
      </Alert>
    );
  }

  // Service Closed / After-Hours Mode (grey/neutral)
  if (!serviceStatus.isServiceOpen) {
    const experience = serviceStatus.outOfHoursExperience;

    return (
      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: '#E2E3E5',
          color: '#1A1A1A',
          '& .MuiAlert-icon': { color: '#5C6BC0' }
        }}
        icon={<NightsStayIcon />}
      >
        <AlertTitle>After-Hours Support</AlertTitle>
        Full AI Therapist returns {formatDateTime(serviceStatus.nextOpenTime) || 'during service hours'}
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => {/* Open schedule modal */}}
          >
            View Schedule
          </Button>
          {experience?.emergencyContacts?.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PhoneIcon />}
              color="error"
              onClick={() => {/* Open emergency contacts modal */}}
            >
              Emergency Contact
            </Button>
          )}
        </Box>
      </Alert>
    );
  }

  return null;
};

// Helper functions
function formatTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateStr;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleString('en-GB', {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateStr;
  }
}

export default ServiceStatusBanner;
